import { useCallback, useEffect, type ReactNode } from 'react'
import moment from 'moment'
import {
  fetchSubscriptions,
  selectHeardProductSubscription,
  selectSubscription,
  SUBSCRIPTION_DEFAULT_KEY,
} from '@/reducers/subscription.slice'
import { TrialStatus } from '@/reducers/auth/userReducer'

import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import {
  getFreeTrialEndsAt,
  getFreeTrialStatus,
  hasManualRequest,
  isCancelling,
  selectIsInUnservicedFreeTrial,
} from '@/selectors/user.selectors'

import { TypographyStyles } from '@/styles'
import { Alert, Banner, BannerVariants, Table, Text } from '@/ui'
import { centsToCurrency } from '@/utils/currencyHelpers'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { useAnalyticsView } from '@/features/amplitude'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { fetchPayrollProfileIfNeeded } from '@/features/payroll/payrollActions'
import { fetchUserTransactions } from '@/features/transactions/transactions.slice'

import { fetchUserTaxEstimates } from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { fetchAnnualTaxFilingFormsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { fetchNextBillingDate } from '@/actions/settings/billingActions'
import { DateTime } from 'luxon'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'

type Subscription = ReturnType<typeof selectSubscription>[string]

const SubscriptionPrices = ({ sub }: { sub: Subscription }) => (
  <>
    {Object.values(sub.items).map((item) => (
      <p key={item.id}>
        {item.name !== SUBSCRIPTION_DEFAULT_KEY && (
          <>
            {item.name}
            {item.quantity > 1 && <>&nbsp;({item.quantity} total)</>}
            :&nbsp;
          </>
        )}
        <Text>
          <CurrencyFormatLabel
            value={
              centsToCurrency(item.amountInCents).multiply(item.quantity).value
            }
          />
        </Text>
      </p>
    ))}
  </>
)

const StackedField = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) => (
  <div className="flex flex-col gap-1">
    <Text variant={TypographyStyles.EYEBROW} className="text-neutral-500">
      {label}
    </Text>
    <div>{children}</div>
  </div>
)

export const BillingTable = () => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const subscription = useReselector(selectSubscription)
  const primarySubscription = useReselector(selectHeardProductSubscription)
  const membershipIsCancelling = useReselector(isCancelling)
  const membershipHasManualRequest = useReselector(hasManualRequest)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)
  const trialEndsAt = useReselector(getFreeTrialEndsAt)
  const enableStripePlanIncidentMitigation = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableStripePlanIncidentMitigation,
    false
  )
  const nextInvoiceDate = useFetchResponse(fetchNextBillingDate, {
    enabled: enableStripePlanIncidentMitigation,
  })

  /**
   * Users affected by the 7/28/2025 incident will display the wrong next billing date
   * until their subscription renews. This will use the correct date from the subscription
   * schedule. This can be removed after 7/28/2026.
   */
  const getNextBillingDate = useCallback(
    (nextBillingDateInSeconds: number | null) => {
      if (nextInvoiceDate) {
        return DateTime.fromISO(nextInvoiceDate).toFormat(
          DATE_FORMATS_LUXON.DISPLAY_LONG
        )
      }
      if (nextBillingDateInSeconds === null) {
        return 'N/A'
      }
      return DateTime.fromSeconds(nextBillingDateInSeconds).toFormat(
        DATE_FORMATS_LUXON.DISPLAY_LONG
      )
    },
    [nextInvoiceDate]
  )

  const isInActiveFreeTrial =
    isInUnservicedFreeTrial && userFreeTrialStatus === TrialStatus.active

  useEffect(() => {
    pageView('plans and billing page')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchSubscriptions())
    dispatch(fetchPayrollProfileIfNeeded())
    dispatch(fetchUserTransactions())
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchCurrentUserDetailsIfNeeded(true))
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
  }, [dispatch])

  const endingNotices = (
    <>
      {membershipIsCancelling && (
        <Banner variant={BannerVariants.WARNING}>
          Subscription Ending on:{' '}
          {primarySubscription?.nextBillingDate &&
            DateTime.fromSeconds(primarySubscription.nextBillingDate).toFormat(
              DATE_FORMATS_LUXON.DISPLAY_LONG
            )}
        </Banner>
      )}
      {membershipHasManualRequest && (
        <Banner variant={BannerVariants.WARNING}>
          You&#39;ve requested the cancellation of your Heard plan. We&#39;ll
          get back to you as soon as we can.
        </Banner>
      )}
    </>
  )

  if (isMobile) {
    return (
      <div className="flex flex-col gap-6">
        {isInActiveFreeTrial ? (
          <Alert>
            <Text variant={TypographyStyles.BODY_LG}>
              You are previewing the Heard Experience, with{' '}
              {moment(trialEndsAt).diff(moment(), 'days')} days remaining in
              your trial
            </Text>
          </Alert>
        ) : (
          <>
            {Object.values(subscription).map((sub) => (
              <div key={sub.id} className="flex flex-col gap-4">
                <Text variant={TypographyStyles.LABEL_MD}>{sub.name}</Text>
                <StackedField label="Price">
                  <SubscriptionPrices sub={sub} />
                </StackedField>
                <StackedField label="Billing start date">
                  {moment.unix(sub.startDate).format(DATE_FORMATS.DISPLAY_LONG)}
                </StackedField>
                <StackedField label="Next billing date">
                  {getNextBillingDate(sub.nextBillingDate)}
                </StackedField>
              </div>
            ))}
            {endingNotices}
          </>
        )}
      </div>
    )
  }

  return (
    <Table className="[&_tbody_tr]:border-b-0">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Plan</Table.HeaderCell>
          <Table.HeaderCell>Price</Table.HeaderCell>
          <Table.HeaderCell>Billing Start Date</Table.HeaderCell>
          <Table.HeaderCell>Next Billing Date</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      {isInActiveFreeTrial && (
        <Table.Body>
          <Table.Row>
            <Table.Cell colSpan={4}>
              <Alert>
                <Text variant={TypographyStyles.BODY_LG}>
                  {' '}
                  You are previewing the Heard Experience, with{' '}
                  {moment(trialEndsAt).diff(moment(), 'days')} days remaining in
                  your trial
                </Text>
              </Alert>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      )}

      {!isInActiveFreeTrial && (
        <Table.Body>
          {Object.values(subscription).map((sub) => (
            <Table.Row key={sub.id}>
              <Table.Cell>{sub.name}</Table.Cell>
              <Table.Cell>
                <SubscriptionPrices sub={sub} />
              </Table.Cell>

              <Table.Cell>
                {moment.unix(sub.startDate).format(DATE_FORMATS.DISPLAY_LONG)}
              </Table.Cell>

              <Table.Cell>{getNextBillingDate(sub.nextBillingDate)}</Table.Cell>
            </Table.Row>
          ))}

          {membershipIsCancelling && (
            <Table.Row>
              <Table.Cell colSpan={4}>
                <Banner variant={BannerVariants.WARNING}>
                  Subscription Ending on:{' '}
                  {primarySubscription?.nextBillingDate &&
                    DateTime.fromSeconds(
                      primarySubscription.nextBillingDate
                    ).toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
                </Banner>
              </Table.Cell>
            </Table.Row>
          )}

          {membershipHasManualRequest && (
            <Table.Row>
              <Table.Cell colSpan={4}>
                <Banner variant={BannerVariants.WARNING}>
                  You&#39;ve requested the cancellation of your Heard plan.
                  We&#39;ll get back to you as soon as we can.
                </Banner>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      )}
    </Table>
  )
}
